import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../core/auth'
import { PREVIEW_ROLES, PREVIEW_TOOLS } from '../core/preview'
import { SCOPE_LABELS } from '../core/caps'
import { MODULES } from '../core/modules'

/**
 * The top-right menu.
 *
 * Deliberately NOT a hamburger. A hamburger promises navigation, and navigation
 * already has two homes — the tiles on the home screen and the rail on desktop.
 * Adding a third would just hide things people can already see.
 *
 * What actually had nowhere to live is "me and this app": who am I signed in as,
 * how do I get out, how do I install this, and the handful of admin screens that
 * are too infrequent to earn a permanent slot.
 */
export function AccountMenu() {
  const { profile, student, displayName, user, signOutNow, can, canPreview, previewTools, preview, setPreview } = useAuth()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const install = useInstallPrompt()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onClick = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  if (!profile && !student) return null

  const initials = (displayName || user?.email || '')
    .split(/[\s.@]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')

  const places = profile?.buildings ?? []
  // Who you are, in words: "District admin · High School", not "district · hs".
  const whoLine = [
    profile?.districtAdmin && 'District admin',
    ...places.filter((p) => p !== 'district').map((p) => SCOPE_LABELS[p] ?? p),
  ].filter(Boolean).join(' · ')
  // Inside a tool, only that tool's preview roles; elsewhere, every tool you
  // may preview, each under its own heading.
  const here = MODULES.find((m) => pathname === m.path || pathname.startsWith(m.path + '/'))?.id
  const tools = PREVIEW_TOOLS.filter((t) => previewTools.includes(t.tool) && (!here || here === t.tool || !previewTools.includes(here)))

  return (
    <div className="acct" ref={wrap}>
      <button
        className="acct__btn"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">{initials}</span>
        <span className="visually-hidden">Account and settings</span>
      </button>

      {open && (
        <div className="acct__panel" role="menu">
          <div className="acct__who">
            <div style={{ fontWeight: 620 }}>{displayName}</div>
            {/* Shown on purpose: people have a school account and a personal one,
                and "why can't I see anything" is almost always the wrong one. */}
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', wordBreak: 'break-all' }}>
              {user?.email}
            </div>
            {whoLine && (
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 4 }}>
                {whoLine}
              </div>
            )}
          </div>

          {can('admin.grant') && (
            <>
              <div className="acct__label">Manage</div>
              <Link className="acct__item" to="/admin/accounts" role="menuitem" onClick={() => setOpen(false)}>
                Accounts &amp; permissions
              </Link>
            </>
          )}

          {/* By the real record, so it is still here while previewing, which
              is how you get back out. One group per tool: "Admin" is a
              different job in each. */}
          {canPreview && (
            <>
              {tools.map((t) => (
                <div key={t.tool} role="group" aria-label={`View ${t.label} as`}>
                  <div className="acct__label">View {t.label} as…</div>
                  {PREVIEW_ROLES.filter((r) => r.tool === t.tool).map((r) => (
                    <button key={r.k} className={`acct__item${preview === r.k ? ' is-on' : ''}`} role="menuitemradio"
                      aria-checked={preview === r.k}
                      onClick={() => { setPreview(r.k); setOpen(false) }}>
                      {r.label}{preview === r.k && ' ✓'}
                    </button>
                  ))}
                </div>
              ))}
              {preview && (
                <button className="acct__item" role="menuitem" onClick={() => { setPreview(null); setOpen(false) }}>
                  Stop preview
                </button>
              )}
            </>
          )}

          {/* Staff only. A student's whole app is their NorseTerm screen, and
              the directory is staff contact details the rules keep from them. */}
          {profile && (
            <>
              <div className="acct__sep" />
              <Link className="acct__item" to="/roles" role="menuitem" onClick={() => setOpen(false)}>
                Roles &amp; responsibilities
              </Link>
              <Link className="acct__item" to="/directory" role="menuitem" onClick={() => setOpen(false)}>
                Staff directory
              </Link>
            </>
          )}

          {install.canInstall && (
            <button className="acct__item" role="menuitem"
              onClick={() => { install.prompt(); setOpen(false) }}>
              Add to Home Screen
            </button>
          )}
          {install.iosHint && (
            <div className="acct__hint">
              To install: tap Share, then <strong>Add to Home Screen</strong>.
            </div>
          )}

          <div className="acct__sep" />
          <button className="acct__item" role="menuitem" onClick={() => signOutNow()}>
            Sign out
          </button>

          {/* First question anyone asks when something is broken. Costs one line. */}
          <div className="acct__hint">SailNorse v{__APP_VERSION__}</div>
        </div>
      )}
    </div>
  )
}

/**
 * Chrome and Android fire beforeinstallprompt and let us trigger the install.
 * iOS never does — Safari only installs via the Share sheet — so there we show
 * the instruction instead of pretending we have a button. Either way this beats
 * hoping somebody finds it on their own.
 */
function useInstallPrompt() {
  const [deferred, setDeferred] = useState<{ prompt: () => void } | null>(null)
  const [standalone] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true)

  useEffect(() => {
    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e as unknown as { prompt: () => void }) }
    window.addEventListener('beforeinstallprompt', onPrompt)
    return () => window.removeEventListener('beforeinstallprompt', onPrompt)
  }, [])

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  return {
    canInstall: !!deferred && !standalone,
    iosHint: isIOS && !standalone,
    prompt: () => deferred?.prompt(),
  }
}
